import Link from 'src/components-v2/Inputs/Link/Link';
import DocsTemplate from 'src/templates/DocsTemplate/DocsTemplate';
import Spacer from 'src/components-v2/Layout/Spacer/Spacer';

const metaData = {
  layout: 'docs',
  'meta-title': 'Privacy Policy',
  'meta-description': 'Plaid Check Privacy Policy',
  'hide-search': 'true',
  'subnav-filters-hidden': 'true',
  noIndex: true,
};

// When editing the contents of this page, be sure to update
// /cra/en/legal/privacy-policy accordingly

const PrivacyPolicy = () => {
  return (
    <DocsTemplate {...metaData}>
      <div className='bg-docs'>
        <div id='docsWrapper' className='grid-container'>
          <div className='grid-x'>
            <aside id='docsSidenav' className='cell large-3 sidenav-docs'>
              <div
                className='sticky show-for-large'
                data-sticky
                data-top-anchor='docsSidenav:top'
                data-btm-anchor='footer:top'
                data-margin-top='8'
                data-margin-bottom='1'
                data-check-every='0'
              >
                <ul
                  id='desktopSidenavMenu'
                  className='menu sidenav-menu vertical'
                  data-sidenav-toggle
                ></ul>
              </div>
            </aside>
            <article
              id='docsArticle'
              className='cell small-12 large-auto article-docs'
            >
              <div className='article-docs-content'>
                <Spacer height={100} />
                <h2>Plaid Consumer Reporting Agency End User Privacy Policy</h2>
                <p>Effective Date: December 2025</p>
                <p>
                  Privacy and security are very important to us at Plaid
                  Consumer Reporting Agency, Inc. dba Plaid Check (“Plaid CRA”).
                  This Privacy Policy (“Policy”) is meant to help you understand
                  how we at Plaid CRA collect, use, and share your data when you
                  use Plaid CRA products or services – for example, when Plaid
                  CRA is used to create and share a Plaid CRA consumer report
                  based on your data with users of consumer reports such as
                  lenders and landlords (“Consumer Report Users”).
                </p>
                <p>
                  This Policy applies to Plaid CRA and explains your privacy
                  rights and the ways in which Plaid CRA lawfully uses, retains,
                  and shares consumer data to provide, improve, and test our
                  consumer reporting and scoring services in compliance with the
                  Fair Credit Reporting Act (“FCRA”), Gramm-Leach-Bliley Act
                  (“GLBA”), and other applicable laws.
                </p>
                <p>
                  This Policy does not cover data we collect through our
                  websites or when you interact with Plaid CRA outside of using
                  our product or services, such as emailing Plaid CRA directly.
                  Please see our{' '}
                  <Link href='/legal/#all-audiences'>
                    <a>All Audience Privacy Statement</a>
                  </Link>{' '}
                  and{' '}
                  <Link href='/legal/#cookie-policy'>
                    <a>Cookie Policy</a>
                  </Link>{' '}
                  for more information. This Policy also does not cover how
                  Plaid Inc. collects, uses, and shares your data when you use
                  Plaid Inc. products or services. You can find that information
                  in{' '}
                  <Link href='/legal/#end-user-privacy-policy'>
                    <a>Plaid Inc.’s End User Privacy Policy</a>
                  </Link>
                  . In addition, this Policy does not cover what Consumer Report
                  Users do with your data or consumer report. You should review
                  the privacy policies or terms of service for those Consumer
                  Report Users for information about their practices.
                </p>
                <p>
                  <em>Jump to section:</em>
                </p>
                <p>
                  <Link href='#our-data-practices'>
                    <a>Our Data Practices</a>
                  </Link>
                </p>
                <ul>
                  <li>
                    <p>
                      <Link href='#data-we-collect-and-categories-of-sources'>
                        <a>Data We Collect and Categories of Sources</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#how-we-use-your-data'>
                        <a>How We Use Your Data</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#how-we-share-your-data'>
                        <a>How We Share Your Data</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#our-retention-and-deletion-practices'>
                        <a>Our Retention and Deletion Practices</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#protection-of-data'>
                        <a>Protection of Data</a>
                      </Link>
                    </p>
                  </li>
                </ul>
                <p>
                  <Link href='#some-final-details'>
                    <a>Some Final Details&hellip;</a>
                  </Link>
                </p>
                <ul>
                  <li>
                    <p>
                      <Link href='#your-data-protection-rights'>
                        <a>Your Data Protection Rights</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#changes-to-this-policy'>
                        <a>Changes To This Policy</a>
                      </Link>
                    </p>
                  </li>
                  <li>
                    <p>
                      <Link href='#contacting-plaid-cra'>
                        <a>Contacting Plaid CRA</a>
                      </Link>
                    </p>
                  </li>
                </ul>
                <h4 id='our-data-practices'>Our Data Practices</h4>
                <h5 id='data-we-collect-and-categories-of-sources'>
                  Data We Collect and Categories of Sources
                </h5>
                <p>
                  The data we collect, use, retain, or share depends on the
                  Plaid CRA products and services that you, and/or the app you
                  have connected to, use. Depending on the products or services
                  used, Plaid CRA may collect the following:
                </p>
                <ul>
                  <li>Data you provide to us;</li>
                  <li>
                    Data from financial institutions when you connect your
                    financial account(s);
                  </li>
                  <li>
                    Data from our affiliates to provide you with a better user
                    experience and improve and evaluate Plaid CRA products and
                    services;
                  </li>
                  <li>
                    Data from the electronic device you use to connect your
                    financial account(s) using Plaid CRA or to otherwise
                    interact with Plaid CRA;
                  </li>
                  <li>Data from Consumer Report Users; and/or</li>
                  <li>
                    Data from other sources, including service providers or
                    identity verification and fraud prevention services.
                  </li>
                </ul>
                <p>
                  Plaid CRA’s services are not directed to individuals under 18
                  and we do not knowingly collect data relating to children.
                </p>
                <p>
                  <strong>Data you provide to us.</strong> When you interact
                  with our{' '}
                  <Link href='/check/consumer-service-center'>
                    <a>Consumer Support Center</a>
                  </Link>{' '}
                  or use Plaid CRA’s products or services, such as to create and
                  share a consumer report, we may collect the following data
                  from you:
                </p>
                <ul>
                  <li>
                    <strong>identifiers</strong> like name, email address, phone
                    number, date of birth, and last four digits of Social
                    Security number;
                  </li>
                  <li>
                    <strong>login data</strong> when required by the provider of
                    your account, like your username and password, account and
                    routing number, or a security token;
                  </li>
                  <li>
                    any additional information needed{' '}
                    <strong>
                      to help verify your identity, connect your accounts, or
                      locate your records
                    </strong>
                    , including security questions and answers and one-time
                    password (OTP);
                  </li>
                  <li>
                    information from <strong>documents or statements</strong>{' '}
                    that you provide to Plaid CRA, like bank statements or pay
                    stubs; and/or
                  </li>
                  <li>
                    information related to{' '}
                    <strong>your use of our services</strong>, including which
                    of our services you use, the dates and times of your use,
                    and which financial institutions and apps you connect using
                    Plaid CRA.
                  </li>
                </ul>
                <p>
                  When you provide login data and additional information needed
                  to connect your accounts, you also give Plaid CRA permission
                  and authority to act on your behalf to access and transmit
                  your data to and from your financial institution.
                </p>
                <p>
                  <strong>Data from our affiliates. </strong>
                  We may receive data from our affiliates (these are companies
                  related to us by common ownership or control) to: (i) provide
                  support to you, improve your experience, or deliver products
                  or services on your behalf; (ii) support compliance, audit,
                  and model-validation functions required by law; and (iii)
                  develop, test, and improve our products and services. For
                  example, if you elect for Plaid Inc. to remember financial
                  accounts associated with your phone number, Plaid CRA may
                  collect the following data from Plaid Inc. in order to help
                  you connect to your accounts faster when using Plaid
                  CRA&rsquo;s services:
                </p>
                <ul>
                  <li>
                    <strong>identifiers </strong>like name, email address, and
                    phone number;
                  </li>
                  <li>
                    <strong>login data </strong>when required by the provider of
                    your account, like your username and password, bank name,
                    account and routing number, or a security token;
                  </li>
                  <li>
                    when needed,{' '}
                    <strong>
                      data to help verify your identity and connect your
                      accounts
                    </strong>
                    , including your date of birth, security questions and
                    answers, and OTP; and/or
                  </li>
                  <li>
                    <strong>data from your device </strong>(as explained in more
                    detail below).
                  </li>
                </ul>
                <p>
                  <strong>
                    Data you provide to us from your financial institutions when
                    you connect your financial account.
                  </strong>{' '}
                  Depending on the Plaid CRA products or services used, as well
                  as what and how information is made available by your
                  financial institutions, we may collect the following data from
                  financial institutions:
                </p>
                <ul>
                  <li>
                    <strong>Account data</strong>, including financial
                    institution name, account name, account type, account
                    ownership, branch number, IBAN, BIC, account number, routing
                    number, and sort code;
                  </li>
                  <li>
                    <strong>Data about an account balance</strong>, including
                    current and available balance;
                  </li>
                  <li>
                    <strong>Data about credit accounts</strong>, including due
                    dates, balances owed, payment amounts and dates, transaction
                    history, credit limit, repayment status, and interest rate;
                  </li>
                  <li>
                    <strong>Data about loan accounts</strong>, including due
                    dates, repayment status, balances, payment amounts and
                    dates, interest rate, guarantor, loan type, payment plan,
                    and terms;
                  </li>
                  <li>
                    <strong>Data about investment accounts</strong>, including
                    transaction information, type of asset, identifying details
                    about the asset, quantity, price, fees, and cost basis;
                  </li>
                  <li>
                    <strong>Identifiers </strong>and data about the account
                    owner(s), including name, email address, phone number, date
                    of birth, and address information;
                  </li>
                  <li>
                    <strong>Data about account transactions</strong>, including
                    amount, date, payee, type, quantity, price, location,
                    involved securities, and a description of the transaction;
                    and/or
                  </li>
                  <li>
                    <strong>Data from your payroll and tax documents</strong>,
                    including data about your income and employer, in cases
                    where you&rsquo;ve connected your payroll accounts or
                    provided us with your pay stub or tax form information.
                  </li>
                </ul>
                <p>
                  Depending on the Plaid CRA product or service used, and the
                  manner in which the data is made available, the data collected
                  from your financial accounts may include data from all
                  accounts (e.g., checking, savings, credit card, and joint
                  accounts) accessible through a single set of account
                  credentials.
                </p>
                <p>
                  <strong>Data we receive from your devices.</strong> When you
                  use a device, like your smartphone, tablet, or computer, to
                  interact with our services (including through a Consumer
                  Report User&rsquo;s application), we may collect the following
                  data about that device:
                </p>
                <ul>
                  <li>internet protocol (IP) address;</li>
                  <li>timezone setting and location, device location;</li>
                  <li>hardware model and operating system;</li>
                  <li>features within our services you access;</li>
                  <li>browser data;</li>
                  <li>network data; and/or</li>
                  <li>
                    other technical data about the device (such as settings and
                    preferences).
                  </li>
                </ul>
                <p>
                  <strong>
                    Data we receive about you from Consumer Report Users.{' '}
                  </strong>
                  Consumer Report Users may provide us with identifiers and
                  commercial information about you, like your name, mailing
                  address, email address, phone number, date of birth, last four
                  digits of Social Security number, or information about your
                  financial accounts and transactions.
                </p>
                <p>
                  <strong>Data we receive about you from other sources.</strong>{' '}
                  We may receive data about you directly from third parties, for
                  example, we may receive data from your wireless carrier to
                  help identify your device or from identity verification and
                  fraud prevention services.
                </p>
                <p>
                  <strong>
                    Information we derive from the data we collect.
                  </strong>{' '}
                  We may derive additional information about you from the data
                  we collect. For example, we may infer your geolocation, your
                  annual income, your employer, or the type of account or
                  subaccount you&rsquo;ve chosen to connect (such as whether the
                  account is for a mortgage, student loan, or credit card).
                </p>
                <h5 id='how-we-use-your-data'>How We Use Your Data</h5>
                <p>
                  We use your data for the following business and commercial
                  purposes:
                </p>
                <ul>
                  <li>
                    <strong>Provide Services:</strong> To operate, provide,
                    service, assess, process, and maintain our products and
                    services, such as creating consumer reports about you and
                    providing scores to Consumer Report Users.
                  </li>
                  <li>
                    <strong>Develop Existing Services:</strong> To improve,
                    enhance, modify, add to, and further develop our products
                    and services.
                  </li>
                  <li>
                    <strong>
                      Help Prevent Fraud, Verify Your Identity, or Protect
                      Privacy:
                    </strong>{' '}
                    To verify your identity and help protect you, Consumer
                    Report Users, financial institutions, our partners, Plaid
                    CRA, and others from fraud, malicious activity, and other
                    privacy and security-related concerns.
                  </li>
                  <li>
                    <strong>Develop New Services:</strong> To develop new
                    products, risk and scoring models, and services for use in
                    connection with credit underwriting, portfolio management,
                    and other lawful purposes under the FCRA, including to
                    develop, train, test, and deploy artificial intelligence
                    (&ldquo;AI&rdquo;) systems.
                  </li>
                  <li>
                    <strong>Develop Insights:</strong> To develop insights based
                    on the data we&rsquo;ve collected about you. This includes
                    your transaction data, other financial data, data about
                    which financial accounts you have connected to which apps,
                    and data from other sources, to help us, your financial
                    institutions, and the developers of your connected apps
                    provide products and services and/or a better user
                    experience to you, like providing you with a faster
                    connection and onboarding experience or helping to detect
                    and prevent potentially fraudulent activity.
                  </li>
                  <li>
                    <strong>Provide Support:</strong> To provide support to you
                    or to Consumer Report Users, including to help respond to
                    your inquiries related to our services and prepare
                    disclosures that you may request or to facilitate
                    investigation of disputes that you initiate under the Fair
                    Credit Reporting Act.
                  </li>
                  <li>
                    <strong>Investigate Misuse and Misconduct:</strong> To
                    investigate misuse of services, including violations of our
                    terms of use (such as our{' '}
                    <Link href='/legal/#developer-policy'>
                      <a>Developer Policy</a>
                    </Link>
                    ), criminal activity, or other unauthorized access to our
                    services.
                  </li>
                  <li>
                    <strong>For Legal Purposes:</strong> To comply with and to
                    further compliance with contractual and legal obligations
                    under applicable law and for other legal purposes such as to
                    establish and defend against claims.
                  </li>
                  <li>
                    <strong>To Communicate with You:</strong> To communicate
                    with you and send you things like technical notices,
                    updates, security alerts, and other messages.
                  </li>
                  <li>
                    <strong>
                      For Testing, Research, and Model Validation:
                    </strong>{' '}
                    To retrospectively test, validate, and improve the accuracy
                    and predictiveness of our consumer reporting and scoring
                    products, consistent with FCRA obligations and other
                    applicable laws.
                  </li>
                  <li>
                    <strong>With Your Consent:</strong> For other notified
                    purposes with your consent or at your direction.
                  </li>
                </ul>
                <p>
                  We may also collect, use, retain, and share data that has been
                  deidentified or aggregated in a manner that does not identify
                  you personally for any purpose permitted under applicable law.
                  This includes creating or using deidentified or aggregated
                  data to develop new products or services, to facilitate
                  research, and to help us or our customers assess the speed,
                  accuracy, performance, and/or security of our services.
                </p>
                <h5 id='how-we-share-your-data'>How We Share Your Data</h5>
                <p>As permitted by law, we may share your data as follows:</p>
                <ul>
                  <li>
                    With Consumer Report Users and as directed by that user;
                  </li>
                  <li>With your connected financial institutions;</li>
                  <li>To enforce any contract with you;</li>
                  <li>
                    With our data processors and other service providers,
                    partners, agents, or contractors in connection with the
                    services they perform for us or Consumer Report Users;
                  </li>
                  <li>
                    If we believe in good faith that disclosure is appropriate
                    to comply with applicable law, regulation, or legal process
                    (like a court order or subpoena) or as required to respond
                    to a request by a regulator that has authority over us, a
                    government authority or law enforcement authority;
                  </li>
                  <li>
                    In connection with a change in ownership or control of all
                    or a part of our business (like a merger, acquisition,
                    reorganization, or bankruptcy);
                  </li>
                  <li>
                    Between and among Plaid CRA and our current and future
                    parents, affiliates, subsidiaries, and other companies under
                    common control or ownership;
                  </li>
                  <li>
                    As we believe reasonably appropriate to protect against and
                    prevent actual or potential fraud, unauthorized
                    transactions, claims, other liabilities, or otherwise
                    protect the rights, privacy, safety, or property of you,
                    Consumer Report Users, our partners, Plaid CRA, and others;
                    or
                  </li>
                  <li>
                    For any other notified purpose with your consent or at your
                    direction.
                  </li>
                </ul>
                <h5 id='prescreening-and-permissible-purposes-under-fcra'>
                  Prescreening and Permissible Purposes under FCRA
                </h5>
                <p>
                  Plaid CRA provides consumer report information to third
                  parties for permissible purposes under the FCRA, this may
                  include prescreening for firm offers of credit or insurance.
                  Prescreening helps Consumer Report Users evaluate your
                  creditworthiness and match you with certain offers for which
                  you may qualify. You have the right to opt out of this data
                  sharing for prescreened offers. To do so, you can visit our{' '}
                  <Link href='/check/consumer-service-center'>
                    <a>Consumer Service Center</a>
                  </Link>{' '}
                  or contact us at 844-204-5860.
                </p>
                <p>
                  When you link your financial accounts through Plaid CRA, we
                  typically use Google&rsquo;s reCaptcha service to help detect
                  fraud and abuse. Google reCAPTCHA is integrated into Plaid
                  products and services in order to ensure that entries made in
                  online forms, for example, are actually made by real persons
                  and are not automated by software (or bots).
                </p>
                <p>
                  Google reCAPTCHA analyzes the behavior of users of our
                  products and services using different characteristics. Google
                  reCAPTCHA processes personal data such as your IP address,
                  your length of stay on our website and further information
                  about your use of Plaid products and services. This data will
                  be transmitted and stored by Google on servers in the United
                  States and other countries. Google has certified with the
                  EU-US Data Privacy Framework; the certificate is available{' '}
                  <Link href='https://www.dataprivacyframework.gov/s/participant-search/participant-detail?id=a2zt000000001L5AAI&status=Active'>
                    <a target='_blank'>here</a>
                  </Link>
                  . When reCaptcha is used,{' '}
                  <Link href='https://policies.google.com/privacy?hl=en'>
                    <a target='_blank'>Google&rsquo;s Privacy Policy</a>
                  </Link>{' '}
                  and{' '}
                  <Link href='https://policies.google.com/terms?hl=en'>
                    <a target='_blank'>Terms of Use</a>
                  </Link>{' '}
                  apply to reCaptcha and information Google collects through
                  reCaptcha.
                </p>
                <p>
                  We may collect and share cookie data with third parties when
                  you visit our website, or we may allow third parties to
                  collect this cookie data from our sites. Please see our{' '}
                  <Link href='/legal/#cookie-policy'>
                    <a>Cookie Policy</a>
                  </Link>{' '}
                  and{' '}
                  <Link href='/legal/#all-audiences'>
                    <a>All Audience Privacy Statement</a>
                  </Link>{' '}
                  for more details.
                </p>
                <h5 id='our-retention-and-deletion-practices'>
                  Our Retention and Deletion Practices
                </h5>
                <p>
                  We retain your data only as long as necessary to fulfill
                  legal, contractual, and operational purposes. The exceptions
                  to this may be if: (a) you&rsquo;ve established a connection
                  with a Consumer Report User through Plaid CRA; (b) Plaid CRA
                  needs your data to continue providing a Plaid CRA product or
                  service you requested; (c) Plaid CRA is required by law to
                  keep your data or retention is required by our compliance
                  program to show compliance with law, including the Fair Credit
                  Reporting Act; (d) Plaid CRA needs your data to help protect
                  against or prevent fraud or protect privacy, provide support,
                  or investigate misuse and misconduct; (e) Plaid has
                  de-identified or aggregated your data such that it cannot be
                  reasonably reidentified; or (f) we request &ndash; and you
                  specifically agree &ndash; to allow us to retain your data
                  longer.
                </p>
                <p>
                  Your data will only be accessed, shared, and retained as
                  required by law or in accordance with this Policy.
                </p>
                <p>
                  Please refer to the &ldquo;Your Data Protection Rights&rdquo;
                  section of this Policy for options that may be available to
                  you, including how to request deletion of your data.
                </p>
                <h5 id='protection-of-data'>Protection of Data</h5>
                <p>
                  Plaid CRA&rsquo;s security policies and practices are designed
                  to protect the confidentiality and integrity of your data.
                  Plaid CRA implements controls designed to limit access to this
                  data to its personnel who have a business reason to know it
                  and prohibits its personnel from unlawfully accessing, using,
                  and disclosing this data.
                </p>
                <h4 id='some-final-details'>Some Final Details</h4>
                <h5 id='your-data-protection-rights'>
                  Your Data Protection Rights
                </h5>
                <p>
                  We welcome and appreciate the chance to address any concerns
                  you may have and encourage you to contact us.
                </p>
                <p>
                  Under the FCRA, you have specific rights regarding your
                  consumer report, including the right to access your file,
                  dispute incomplete or inaccurate information, and request
                  reinvestigation.
                </p>
                <p>
                  If you wish to exercise these rights or initiate a dispute,
                  you can submit a request using our{' '}
                  <Link href='/check/consumer-service-center'>
                    <a>Consumer Service Center</a>
                  </Link>
                  . You can also contact us as described in the
                  &ldquo;Contacting Plaid CRA&rdquo; section below to exercise
                  these rights. You may be required to provide additional
                  information necessary to confirm your identity before we can
                  respond to your request.
                </p>
                <p>
                  If we receive your request from an authorized agent, we may
                  ask for evidence that the agent has valid written authority,
                  like a power of attorney, to submit requests on your behalf.
                </p>
                <p>
                  We will consider requests and provide our response within a
                  reasonable period of time (and within any time period required
                  by applicable law). Please note, however, that certain data
                  may be exempt from such requests, for example if we need to
                  keep the data to comply with our own legal obligations or to
                  establish, exercise, or defend legal claims.
                </p>
                <p>
                  For information about data rights associated with Plaid
                  Inc.&rsquo;s products or services, please visit Plaid
                  Inc.&rsquo;s{' '}
                  <Link href='/legal/#end-user-privacy-policy'>
                    <a>End User Privacy Policy</a>
                  </Link>
                  .
                </p>
                <h5 id='changes-to-this-policy'>Changes To This Policy</h5>
                <p>
                  We may update or change this Policy from time to time. If we
                  make any updates or changes, we will post the new policy on
                  Plaid CRA&rsquo;s website at{' '}
                  <Link href='/plaid-check-consumer-report/privacy-policy'>
                    <a>
                      https://plaid.com/plaid-check-consumer-report/privacy-policy
                    </a>
                  </Link>{' '}
                  and update the effective date at the top of this Policy. We
                  will also notify Consumer Report Users of any material changes
                  in accordance with our developer agreements. If we have an
                  email address for you, we will also send you an email
                  notifying you of the update.
                </p>
                <h5 id='contacting-plaid-cra'>Contacting Plaid CRA</h5>
                <p>
                  If you have any questions or complaints about this Policy, or
                  about our privacy practices generally, you can contact us at
                  844-204-5860, at PlaidCRA@plaid.com, or by mail at:
                </p>
                <p>
                  Plaid CRA
                  <br />
                  Attn: Legal
                  <br />
                  P.O. Box 7775 #35278
                  <br />
                  San Francisco, California 94120-7775
                  <br />
                  U.S.A.
                </p>
                <hr
                  style={{
                    color: '#383838',
                    backgroundColor: '#383838',
                    height: 0,
                    borderColor: '#383838',
                    margin: '48px 0',
                  }}
                />
                <h2>Consumer Privacy Notice</h2>
                <p>Last Updated: December 2025</p>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td>&nbsp;</td>
                        <td>
                          <strong>
                            WHAT DOES PLAID CONSUMER REPORTING AGENCY, INC. DO
                            WITH YOUR PERSONAL INFORMATION?
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Why?</strong>
                        </td>
                        <td>
                          Financial companies choose how they share your
                          personal information. Federal law gives consumers the
                          right to limit some but not all sharing. Federal law
                          also requires us to tell you how we collect, share,
                          and protect your personal information. Please read
                          this notice carefully to understand what we do.
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>What?</strong>
                        </td>
                        <td>
                          The types of personal information we collect and share
                          depend on the product or service used. This
                          information can include:
                          <ul>
                            <li>Identity information and account balances</li>
                            <li>
                              Account transactions and transaction history
                            </li>
                            <li>Income and employment information</li>
                          </ul>
                          For more information on the types of personal
                          information we collect, please see the Data We Collect
                          and Categories of Sources section of the Privacy
                          Policy. When you are <em>no longer</em> our consumer,
                          we continue to use and share your information as
                          described in this notice
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>How?</strong>
                        </td>
                        <td>
                          All financial companies need to share consumers&rsquo;
                          personal information to provide their products and
                          services. In the section below, we list the reasons
                          financial companies can share their consumers&rsquo;
                          personal information; the reasons Plaid CRA chooses to
                          share; and whether you can limit this sharing.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <strong>
                            Reasons we can share your personal information
                          </strong>
                        </td>
                        <td>
                          <strong>Does Plaid CRA share?</strong>
                        </td>
                        <td>
                          <strong>Can you limit this sharing?</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            For our everyday business purposes &ndash;
                          </strong>{' '}
                          such as to connect and share data from your financial
                          accounts to power the apps you use, as well as to
                          protect against fraud or misuse, respond to court
                          orders and legal investigations, or report to credit
                          bureaus.
                        </td>
                        <td>Yes</td>
                        <td>No</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>For our marketing purposes</strong>
                        </td>
                        <td>No</td>
                        <td>We don&rsquo;t share</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            For joint marketing with other financial companies
                          </strong>
                        </td>
                        <td>No</td>
                        <td>We don&rsquo;t share</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            For our affiliates' everyday business purposes -
                            information about transactions and experiences
                          </strong>
                        </td>
                        <td>Yes</td>
                        <td>
                          Yes, and we only share with your consent or at your
                          direction
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            For our affiliates' everyday business purposes -
                            information about creditworthiness
                          </strong>
                        </td>
                        <td>No</td>
                        <td>We do not share</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>For nonaffiliates to market to you</strong>
                        </td>
                        <td>Yes</td>
                        <td>Yes</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <strong>To Limit our Sharing</strong>
                        </td>
                        <td>
                          Visit us online:
                          https://plaid.com/check/consumer-service-center/
                          <br />
                          <br />
                          Please note:
                          <br />
                          If you are a new customer, we can begin sharing your
                          information 30 days from the date we sent this notice.
                          When you are no longer our customer, we continue to
                          share your information as described in this notice.
                          <br />
                          <br />
                          However, you can contact us at any time to limit our
                          sharing.
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Question</strong>
                        </td>
                        <td>
                          Contact us{' '}
                          <Link href='mailto:privacy@plaid.com'>
                            <a>Privacy@plaid.com</a>
                          </Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan={2}>
                          <strong>Who we are</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Who is providing this notice?</strong>
                        </td>
                        <td>Plaid Consumer Reporting Agency, Inc.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan={2}>
                          <strong>What we do</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            How does Plaid CRA protect my personal information?
                          </strong>
                        </td>
                        <td>
                          To protect your personal information from unauthorized
                          access and use, we use security measures that comply
                          with federal law. These measures include computer
                          safeguards and secured files and buildings.
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>
                            How does Plaid CRA collect my personal information?
                          </strong>
                        </td>
                        <td>
                          As explained in more detail in the Data We Collect and
                          Categories of Sources section of the Privacy Policy,
                          we collect personal information you provide to us and
                          collect your personal information from other companies
                          when you use our products or services.
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Why can&rsquo;t I limit all sharing?</strong>
                        </td>
                        <td>
                          Federal law gives you the right to limit only:
                          <ul>
                            <li>
                              sharing for affiliates&rsquo; everyday business
                              purposes &ndash; information about your
                              creditworthiness;
                            </li>
                            <li>
                              affiliates from using your information to market
                              to you;
                            </li>
                            <li>sharing for nonaffiliates to market to you.</li>
                          </ul>
                          State laws and individual companies may give you
                          additional rights to limit sharing. See below for more
                          on your rights under state law.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan={2}>
                          <strong>Definitions</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Affiliates</strong>
                        </td>
                        <td>
                          Companies related by common ownership or control. They
                          can be financial and nonfinancial companies.
                          <ul>
                            <li>
                              <em>
                                Our affiliates include Plaid Inc., Plaid
                                Financial Ltd., and Plaid, B.V.
                              </em>
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Nonaffiliates</strong>
                        </td>
                        <td>
                          Companies not related by common ownership or control.
                          They can be financial and nonfinancial companies.
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Joint marketing</strong>
                        </td>
                        <td>
                          A formal agreement between nonaffiliated financial
                          companies that together market financial products or
                          services to you.
                          <ul>
                            <li>
                              <em>Plaid CRA does not jointly market.</em>
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan={2}>
                          <strong>Other Important Information</strong>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <strong>California: </strong>If you are a resident of
                          California, we will not share personal information we
                          collect about you except to the extent permitted under
                          California law.
                          <br />
                          <br />
                          <strong>Vermont: </strong>If you are a resident of
                          Vermont, we will not share personal information we
                          collect about you with non-affiliates unless the law
                          allows or you provide authorization.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </DocsTemplate>
  );
};

export default PrivacyPolicy;
